import { apiClient } from '../api/apiClient';

export interface NutritionChartDataPoint {
  date: string;
  calories: number;
  protein_g: number;
  fat_g: number;
  carbs_g: number;
  fiber_g: number;
  sugar_g: number;
  sodium_mg: number;
  meal_count: number;
  // Vitamins
  vitamin_a_mcg: number;
  vitamin_c_mg: number;
  vitamin_d_mcg: number;
  vitamin_e_mg: number;
  vitamin_k_mcg: number;
  vitamin_b1_mg: number;
  vitamin_b2_mg: number;
  vitamin_b3_mg: number;
  vitamin_b6_mg: number;
  vitamin_b12_mcg: number;
  folate_mcg: number;
  // Minerals
  calcium_mg: number;
  iron_mg: number;
  magnesium_mg: number;
  phosphorus_mg: number;
  potassium_mg: number;
  zinc_mg: number;
  copper_mg: number;
  manganese_mg: number;
  selenium_mcg: number;
}

export interface NutritionChartData {
  data_points: NutritionChartDataPoint[];
  granularity: string;
  start_date: string;
  end_date: string;
  total_days: number;
  avg_daily_calories: number;
  avg_daily_protein_g: number;
  avg_daily_fat_g: number;
  avg_daily_carbs_g: number;
  avg_daily_fiber_g: number;
  avg_daily_sugar_g: number;
  avg_daily_sodium_mg: number;
  total_meals: number;
}

export interface NutritionDataResponse {
  id: number;
  user_id: number;
  meal_date: string;
  meal_type: string;
  dish_name?: string | null;
  food_item_name?: string | null;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  sugar_g: number;
  sodium_mg: number;
  // Vitamins
  vitamin_a_mcg?: number | null;
  vitamin_c_mg?: number | null;
  vitamin_d_mcg?: number | null;
  vitamin_e_mg?: number | null;
  vitamin_k_mcg?: number | null;
  vitamin_b1_mg?: number | null;
  vitamin_b2_mg?: number | null;
  vitamin_b3_mg?: number | null;
  vitamin_b6_mg?: number | null;
  vitamin_b12_mcg?: number | null;
  folate_mcg?: number | null;
  // Minerals
  calcium_mg?: number | null;
  iron_mg?: number | null;
  magnesium_mg?: number | null;
  phosphorus_mg?: number | null;
  potassium_mg?: number | null;
  zinc_mg?: number | null;
  copper_mg?: number | null;
  manganese_mg?: number | null;
  selenium_mcg?: number | null;
  data_source: string;
  image_url?: string | null;
  notes?: string | null;
}

export interface MealLoggingStatsResponse {
  total_logged_meal_slots: number;
  expected_meals: number;
  reporting_rate_pct: number;
  meals_per_day: number;
  snacks_per_day: number;
}

/** Meal plan summary as returned by the monitoring API */
export interface MonitoringMealPlanSummary {
  plan_id: string;
  patient_id: number;
  mode: string;
  variant?: string | null;
  status: string;
  language: string;
  cuisine_pattern: string;
  meals_per_day: number;
  snacks_per_day: number;
  meal_timing_json?: Record<string, unknown> | null;
  approved_at?: string | null;
  activated_at?: string | null;
  name?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  goal_id?: number | null;
}

/** Version fragment with days in the requested range */
export interface MonitoringMealPlanVersionFragment {
  plan_id: string;
  version_id: string;
  days: Array<{ date: string; items: Array<Record<string, unknown>> }>;
}

/** Per-day weighted compliance from backend */
export interface ComplianceDayItem {
  date: string;
  compliance_pct: number;
}

export interface MonitoringDashboardResponse {
  chart_data: NutritionChartData;
  logged_meals: NutritionDataResponse[];
  meal_logging_stats: MealLoggingStatsResponse;
  meal_plan_summary: MonitoringMealPlanSummary | null;
  meal_plan_version: MonitoringMealPlanVersionFragment | null;
  last_week_compliance_pct: number | null;
  overall_compliance_pct: number | null;
  compliance_by_day: ComplianceDayItem[] | null;
}

class NutritionService {
  /**
   * Get nutrition chart data (daily aggregates) for a user
   * Note: For doctor access, the backend may need to support user_id query parameter
   */
  async getChartData(
    userId: number,
    startDate?: string,
    endDate?: string,
    referenceDate?: string,
    periods: number = 30
  ): Promise<NutritionChartData> {
    const params: any = {
      user_id: userId, // For doctor access - backend may need to support this
      periods,
      granularity: 'daily',
    };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (referenceDate) params.reference_date = referenceDate;

    const res = await apiClient.get<NutritionChartData>('/api/v1/nutrition/chart', { params });
    return res.data;
  }

  /**
   * Get raw nutrition data (logged meals) for a user
   * Note: For doctor access, the backend may need to support user_id query parameter
   */
  async getNutritionData(
    userId: number,
    startDate?: string,
    endDate?: string,
    mealType?: string,
    limit: number = 1000
  ): Promise<NutritionDataResponse[]> {
    const params: any = {
      user_id: userId, // For doctor access - backend may need to support this
      limit,
      granularity: 'daily',
    };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (mealType) params.meal_type = mealType;

    const res = await apiClient.get<NutritionDataResponse[]>('/api/v1/nutrition/data', { params });
    return res.data;
  }

  /**
   * Get meal logging frequency stats (backend-calculated): logged meal slots (distinct meal types
   * per day summed), expected meals from plan config, and reporting rate %.
   */
  async getMealLoggingStats(
    userId: number,
    startDate: string,
    endDate: string,
    goalId?: number
  ): Promise<MealLoggingStatsResponse> {
    const params: Record<string, string | number> = {
      user_id: userId,
      start_date: startDate,
      end_date: endDate,
    };
    if (goalId !== undefined) params.goal_id = goalId;
    const res = await apiClient.get<MealLoggingStatsResponse>('/api/v1/nutrition/meal-logging-stats', { params });
    return res.data;
  }

  /**
   * Single API for the monitoring dashboard: chart, logged meals, meal logging stats,
   * meal plan summary + version (days in range), and optional last-week compliance.
   */
  async getMonitoringDashboard(
    userId: number,
    startDate: string,
    endDate: string,
    options?: { goalId?: number; targetCalories?: number }
  ): Promise<MonitoringDashboardResponse> {
    const params: Record<string, string | number> = {
      user_id: userId,
      start_date: startDate,
      end_date: endDate,
    };
    if (options?.goalId !== undefined) params.goal_id = options.goalId;
    if (options?.targetCalories !== undefined && options.targetCalories > 0) {
      params.target_calories = options.targetCalories;
    }
    const res = await apiClient.get<MonitoringDashboardResponse>('/api/v1/nutrition/monitoring', { params });
    return res.data;
  }
}

export const nutritionService = new NutritionService();

